import React, { useState } from 'react';

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';

import axios from '../../api/axios';

import { FaCalendarAlt } from 'react-icons/fa';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import BottomNav from '../../components/BottomNav';
import HeaderWithBack from '../../components/HeaderWithBack';
import SendEmail from '../../components/modal/SendEmail';
import { useCancelOnlinePrescription, useSendPrescriptionEmail } from '../../hooks/usePrescriptionAction';
import { usePrescriptionDetail } from '../../hooks/usePrescriptionDetail';
import { usePrescriptionOrder } from '../../hooks/usePrescriptionOder';
import { usePrescriptionTracking } from '../../hooks/usePrescriptionTracking';
import { useAuthStore } from '../../stores/authStores';
import '../../styles/styles.css'; // Sesuaikan path jika berbeda
import FullscreenLoading from './Components/FullScreenLoader';
import { defaultAvatarUrl, imageUrl } from '../../utils/assets';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

type TrackingData = {
  order_id: string;
  status: string;
  updated_time: string;
};

const EprescriptionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { prescriptionId } = useParams<{ prescriptionId: string }>();
  const [prescriptionOrder, setPrescriptionOrder] = useState<any>(false);
  const userData = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const email = userData ? userData.userData : null;
  const { data, isLoading, refetch: refetchPrescriptionDetail } = usePrescriptionDetail(prescriptionId, token);

  const [showPdf, setShowPdf] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const { data: orderData } = usePrescriptionOrder(prescriptionId, token);
  const { data: trackingData } = usePrescriptionTracking(prescriptionId, token);
  const sendEmailMutation = useSendPrescriptionEmail();
  const cancelMutation = useCancelOnlinePrescription();

  const prescriptionData = data?.data;
  const encToken = data?.encToken;
  const prescriptionOrderData = orderData;
  const prescriptionOrderTracking: TrackingData[] = trackingData ?? [];

  const publicPdfUrl = prescriptionData?.pdf_url;
  const publicPdfUrlDownload = prescriptionData?.pdf_url_download;

  const pricing = prescriptionOrderData?.pricing;
  const shippingCost = Number(pricing?.shipping_cost || 0);
  const serviceFee = Number(pricing?.fee_service || 0);
  const shippingDiscount = Number(pricing?.shipping_discount || 0);
  const totalBayar = Number(pricing?.total || 0);

  const handleDownloadPdf = () => {
    if (publicPdfUrlDownload) {
      setShowButton(false);
      setHasDownloaded(true);
      const link = document.createElement('a');
      link.href = publicPdfUrlDownload;
      link.download = 'resep_telekonsultasi.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.warn('URL PDF tidak tersedia untuk diunduh.');
    }
  };

  let isExpired = false;
  const orderStatus = prescriptionOrderData?.order?.order_status;
  const paymentStatus = prescriptionOrderData?.transaction?.status_payment;
  const isPaidOrder = orderStatus === 'paid' || paymentStatus === 'payment';
  const order = prescriptionOrderData?.order;
  const transaction = prescriptionOrderData?.transaction;
  const hasNoOrder = !order;
  const canContinuePayment =
    order &&
    (!order.order_status ||
     order.order_status === 'draft' ||
     order.order_status === 'invoice' ||
     order.order_status === 'waiting_payment' ||
     order.order_status === 'pending') &&
    (!transaction ||
     transaction.status_payment === 'draft' ||
     transaction.status_payment === 'pending');
  const isPaid = order?.order_status === 'paid' || transaction?.status_payment === 'payment';

  const [showCancelOnlineModal, setShowCancelOnlineModal] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isTebusSendiriMode, setIsTebusSendiriMode] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Resep yang sudah dibayar online dikirim via K24, jadi PDF & tombol download hanya untuk tebus sendiri
  const shouldShowPdf = (isTebusSendiriMode || hasDownloaded || prescriptionData?.download === 1) && !isPaidOrder;

  React.useEffect(() => {
    if (prescriptionData?.download === 1 && !isPaidOrder) {
      setShowPdf(true);
      setShowButton(true);
    }
  }, [prescriptionData?.download, isPaidOrder]);

  if (prescriptionData?.expired) {
    const [day, month, year] = prescriptionData.expired.split("-");
    const expiredDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiredDate.setHours(0, 0, 0, 0);
    isExpired = expiredDate < today;
  }

  const handleSendEmail = () => { setShowModal(true); }

  const handleConfirm = () => {
    if (!prescriptionId || !token) return;
    sendEmailMutation.mutate(
      { prescriptionId, token },
      {
        onSuccess: () => {
          alert("Berhasil mengirim resep");
          setShowButton(false);
          setShowModal(false);
        },
        onError: () => { alert("Gagal mengirim resep"); },
      }
    );
  };

  const handleCheckout = async () => {
    try {
      navigate(`/e-prescription/checkout/${prescriptionId}/${encToken}`);
    } catch (err) {
      alert('Gagal memulai checkout');
    }
  };

  const handleConfirmTebusSendiri = () => {
    if (!prescriptionId || !token) return;
    setIsCancelLoading(true);

    if (prescriptionOrderData?.order) {
      cancelMutation.mutate(
        { prescriptionId, token },
        {
          onSuccess: () => {
            setShowCancelOnlineModal(false);
            setIsTebusSendiriMode(true);
            setShowPdf(true);
            setShowButton(true);
          },
          onError: () => { alert("Gagal membatalkan tebus online"); },
          onSettled: () => { setIsCancelLoading(false); },
        }
      );
    } else {
      setShowCancelOnlineModal(false);
      setIsTebusSendiriMode(true);
      setShowPdf(true);
      setShowButton(true);
      setIsCancelLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col pb-20">
      <HeaderWithBack title="E-Prescription" url="/e-prescription"/>

      <div className="bg-teal-500 text-white px-4 py-2 flex items-center">
        <FaCalendarAlt className="mr-2" />
        <span>Resep Online Anda</span>
      </div>

      {prescriptionData?.expired && (!prescriptionOrder || prescriptionData?.download !== 1) && (
        isExpired ? (
          <div className="bg-red-500 text-white px-4 py-2 text-sm text-center">
            Resep Sudah Kadaluarsa <br /> <strong>{prescriptionData.expired}</strong>
          </div>
        ) : (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-2 text-sm text-center">
            Lakukan penebusan obat sebelum <br /> <strong>{prescriptionData.expired}</strong>
          </div>
        )
      )}

      <div className="px-4 mt-4">
        <div className="bg-white border rounded-md shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-2">Resep Digital</h2>
          <div className="flex items-center border rounded-md p-3 mb-3">
            <img
              src={imageUrl(prescriptionData?.avatar_doctor, defaultAvatarUrl())}
              alt="doctor"
              className="w-10 h-10 rounded-full mr-3"
            />
            <div>
              <p className="font-semibold text-sm">{prescriptionData?.doctor}</p>
              <p className="text-xs text-gray-500">{prescriptionData?.specialty}</p>
            </div>
          </div>

          <div className="flex justify-between items-center text-sm mb-2">
            <p className="font-semibold">R/ {prescriptionData?.code}</p>
            <p className="text-gray-500">Tanggal Konsultasi: <br /> {prescriptionData?.date}</p>
          </div>

          {prescriptionData?.items?.map((item: any, index: number) => (
            <div key={index} className="border px-3 py-2 rounded-md text-sm bg-gray-50 mb-2">
              {item?.drug_name} x {item?.qty} {item?.unit} ({item?.frequency} x {item?.frequency_dd})
            </div>
          ))}

          <div className="mt-4">
            <h3 className="font-semibold text-sm mb-2">Catatan Alergi :</h3>
            { prescriptionData?.allergies.length ? prescriptionData?.allergies?.map((alergi: any, index: number) => (
              <div key={index} className="border rounded-md p-2 mb-2 text-sm">
                <p className="font-semibold">{alergi?.allergy}</p>
                <p className="text-gray-500">Catatan : {alergi?.allergy_note || '-'}</p>
              </div>
            )) : (
              <p className="text-gray-500">Tidak ada catatan alergi</p>
            ) }
          </div>

          {prescriptionData?.log_code && (
            <div className="mt-4">
              <h3 className="font-semibold text-sm mb-2">LOG Code :</h3>
              <div className="border rounded-md p-2 bg-blue-50 min-w-0">
                <p className="text-sm font-mono font-semibold text-blue-800 break-all">{prescriptionData.log_code}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Paket / Estimasi Biaya */}
      {(isPaidOrder || canContinuePayment) && (
        <div className="px-4 mt-4">
          <div className="bg-white border rounded-md shadow-sm p-4">
            <div className="flex items-center mb-3">
              <div className="bg-teal-100 text-teal-600 p-2 rounded-full mr-2">
                <i className="fas fa-box-open"></i>
              </div>
              <h2 className="font-semibold text-gray-800 text-sm">
                {isPaidOrder ? "Detail Paket" : "Estimasi Biaya"}
              </h2>
            </div>

            <div className="text-sm mb-4">
              <p className="font-semibold mb-1">Detail Pesanan :</p>
              <div className="space-y-2">
                {prescriptionData?.items?.map((item: any, index: number) => (
                  <div key={index} className="flex justify-between items-start">
                    <p className="flex-1 pr-2 text-sm">
                      {item?.drug_name}{" "}
                      <span className="text-gray-500">x {item?.qty}</span>
                    </p>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full whitespace-nowrap">Ditanggung</span>
                  </div>
                ))}
              </div>
            </div>

            {pricing ? (
              <div className="text-sm mb-4">
                <div className="flex justify-between">
                  <span>Biaya Obat</span>
                  <span className="text-blue-600">Ditanggung</span>
                </div>
                <div className="flex justify-between">
                  <span>Biaya Pengiriman</span>
                  <span>Rp {shippingCost.toLocaleString("id-ID")}</span>
                </div>
                <div className="flex justify-between">
                  <span>Biaya Layanan</span>
                  <span>Rp {serviceFee.toLocaleString("id-ID")}</span>
                </div>
                {shippingDiscount > 0 && (
                  <div className="flex justify-between">
                    <span>Potongan Ongkos Kirim</span>
                    <span>-Rp {shippingDiscount.toLocaleString("id-ID")}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-1">
                  <span>Total Pesanan</span>
                  <span>Rp {totalBayar.toLocaleString("id-ID")}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                Biaya pengiriman dan layanan akan dihitung di halaman checkout.
              </p>
            )}

            {isPaidOrder && (
              <>
                <div className="text-sm mb-2">
                  <p className="font-semibold mb-1">Metode Pembayaran :</p>
                  <div className="pl-2">
                    <p><span>Biller Code: </span>{prescriptionOrderData.transaction?.merchant_code}</p>
                    <p><span>Bill Key: </span>{prescriptionOrderData.transaction?.va}</p>
                    {prescriptionOrderData.transaction?.status_payment !== "payment" && (
                      <p><span>Bayar Sebelum : </span>{prescriptionOrderData.transaction?.expired_date}</p>
                    )}
                  </div>
                </div>
                <div className="text-sm">
                  <div className="flex justify-between font-bold">
                    <span>Total Bayar:</span>
                    <span>Rp {Number(totalBayar).toLocaleString("id-ID")}</span>
                  </div>
                  <p className="mt-1">
                    <span className="font-bold">Status: </span>
                    {prescriptionOrderData.transaction?.status_payment === "payment" ? (
                      <span className="text-green-600">Sudah Dibayar</span>
                    ) : (
                      <span className="font-bold">Menunggu Pembayaran</span>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isPaidOrder ? (
        <>
          <div className="px-4 mt-4">
            <div className="border shadow rounded bg-white">
              <div className="flex p-4">
                <img src="https://m.linksehat.com/assets/img/icons/delivery-order-icon.png" alt="Delivery Icon" className="h-[30px]" />
                <div className="px-4 self-center text-gray-700 w-full">Detail Pengirim</div>
                <div className="px-4 self-center text-gray-700 ml-auto">Apotek K24</div>
              </div>
              <div className="px-4 pb-4">
                <div><span className="font-bold">{prescriptionOrderData.order?.outlet}</span></div>
                <div><span>{prescriptionOrderData.order?.shipping_method}</span></div>
              </div>
            </div>
          </div>

          <div className="px-4 mt-4">
            <div className="bg-white border rounded-md shadow-sm p-4">
              <div className="flex items-center mb-3">
                <div className="bg-teal-100 text-teal-600 p-2 rounded-full mr-2">
                  <i className="fas fa-truck"></i>
                </div>
                <h2 className="font-semibold text-gray-800 text-sm">Status Pengiriman</h2>
              </div>
              <ul className="text-sm space-y-2 pl-2">
                {isLoading ? (
                  <li className="text-gray-500 italic">Memuat status...</li>
                ) : prescriptionOrderTracking.length > 0 ? (
                  prescriptionOrderTracking.map((data, key) => (
                    <li key={key} className="flex flex-col items-start">
                      <div className="flex items-start">
                        <span className="h-3 w-3 rounded-full mr-2 mt-1 bg-teal-500"></span>
                        <span className="text-gray-700">{data.status}</span>
                      </div>
                      <span className="text-xs text-gray-500 pl-5">{data.updated_time}</span>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500">Belum ada status.</li>
                )}
              </ul>
            </div>
            <div className="mt-4 flex justify-center">
              <a
                href="https://wa.me/6289681773366?text=Halo%20Admin%2C%20saya%20ingin%20bertanya%20tentang%20status%20pengiriman%20obat"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center"
              >
                <i className="fab fa-whatsapp mr-2"></i> HUBUNGI VIA WHATSAPP
              </a>
            </div>
          </div>
        </>
      ) : ''}

      {isLoading && <FullscreenLoading />}

      {!isLoading && !isExpired && !isPaid && (
        <div className="mt-4 p-4 flex gap-2">
          {hasNoOrder && prescriptionData?.download !== 1 && (
            <>
              <button
                onClick={() => navigate(`/e-prescription/address-page/${prescriptionId}/${encToken}`)}
                className="p-2 bg-teal-500 rounded text-white flex items-center"
              >
                Tebus Obat Online
              </button>
              <button
                onClick={() => setShowCancelOnlineModal(true)}
                className="p-2 bg-teal-600 rounded text-white flex items-center"
              >
                Tebus Sendiri
              </button>
            </>
          )}
          {canContinuePayment && (
            <>
              <button
                onClick={() => navigate(`/e-prescription/checkout/${prescriptionId}/${encToken}`)}
                className="p-3 bg-orange-500 text-white rounded font-semibold"
              >
                <i className="fas fa-credit-card mr-2"></i> Lanjutkan Pembayaran
              </button>
              <button
                onClick={() => setShowCancelOnlineModal(true)}
                className="p-2 bg-teal-600 rounded text-white flex items-center"
              >
                Tebus Sendiri
              </button>
            </>
          )}
          {showCancelOnlineModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg w-[90%] max-w-md p-6">
                <h3 className="text-lg font-semibold mb-2">Ganti ke Tebus Sendiri?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Jika kamu melanjutkan, proses tebus online akan dibatalkan dan
                  kamu tidak bisa melanjutkan pembayaran secara online.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    disabled={isCancelLoading}
                    onClick={() => setShowCancelOnlineModal(false)}
                    className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                  >
                    Batal
                  </button>
                  <button
                    disabled={isCancelLoading}
                    onClick={handleConfirmTebusSendiri}
                    className="px-4 py-2 rounded bg-red-600 text-white"
                  >
                    {isCancelLoading ? 'Memproses...' : 'Ya, Tebus Sendiri'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {isExpired && prescriptionData?.download !== 1 && (
        <div className="mt-4">
          <div className="flex p-4">
            <div className="flex">
              <button
                type="submit"
                onClick={() => navigate(`/home`)}
                className="p-2 mr-2 bg-teal-500 rounded text-white whitespace-nowrap flex items-center"
              >
                <i className="fas fa-sync-alt mr-2"></i> Konsultasi Ulang
              </button>
            </div>
          </div>
        </div>
      )}

      {shouldShowPdf ? (
        <div className="mt-4 border rounded p-4 bg-white">
          {isTebusSendiriMode && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="fas fa-exclamation-triangle text-yellow-500"></i>
                <h3 className="text-sm font-semibold">Perhatian!</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">Kamu memilih <b>Tebus Sendiri</b>. Perlu diketahui:</p>
              <ul className="text-sm text-gray-600 space-y-1 pl-4 list-disc">
                <li>Resep akan ditampilkan dan bisa diunduh</li>
                <li className="text-red-600 font-medium">Tebus Online <b>tidak bisa dilakukan</b> setelah ini</li>
                <li>Bawa resep ke apotek terdekat untuk menebus obat</li>
              </ul>
            </div>
          )}
          <Document file={publicPdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
            {Array.from(new Array(numPages), (el, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} width={window.innerWidth - 64} />
            ))}
          </Document>
          {!isExpired && showButton && !isPaidOrder ? (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2 p-4">
                <button onClick={handleDownloadPdf} type="button" className="p-2 bg-teal-600 rounded text-white text-sm flex items-center">
                  <i className="fas fa-file-download mr-2"></i> Download
                </button>
                <button onClick={() => setShowPdf(false)} type="button" className="p-2 bg-gray-500 rounded text-white text-sm flex items-center">
                  <i className="fas fa-times-circle mr-2"></i> Batal
                </button>
                <SendEmail isOpen={showModal} onClose={() => setShowModal(false)} onConfirm={handleConfirm} email={email} />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <BottomNav />
    </div>
  );
};

export default EprescriptionDetail;
